import { useDisclosure, useWindowSize } from '@/hooks';
import { Button, Form, Modal, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import {
  AlterStatusInventory,
  Inventory,
  InventoryFormValues,
  InventoryItemListResponse,
  InventoryItemsResponse,
  InventoryStatus,
  ValidateInventoryResponse,
} from '@/core/domain/inventory/inventories/Inventory.types';
import InventoryReportModalForm from './InventoryReportModalForm';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useHistory } from 'react-router-dom';
import {
  useAlterStatusInventory,
  useNextCountInventory,
  useValidateInventory,
} from '@/core/domain/inventory/inventories/inventory.hooks';
import { FormInstance } from 'antd/es/form/Form';
import InventoryReportP7Modal from './InventoryReportP7Modal';
import InventoryLessCountedThanReservedModal from './InventorylessCountedThanReservedModal';
import { useState } from 'react';
import { useGenerateInventoryReport } from '@/core/domain/reports/stock/Report.hooks';
import {
  ReportInventoryFormValues,
  ReportInventoryParams,
} from '@/core/domain/reports/stock/Report.types';
import { useReportHandlers } from '@/hooks/useReportHandler';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { FormatType } from '@/core/domain/reports/movements/dailyMovement/dailyMovement.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

interface Props {
  inventory?: Inventory;
  inventoryId: number;
  inventoryItemsInfiniteData?: InventoryItemListResponse;
  formInventory: FormInstance<InventoryFormValues>;
}

const InventoryRepeatButtons: React.FC<Props> = ({
  inventory,
  inventoryId,
  inventoryItemsInfiniteData,
  formInventory,
}) => {
  const [lessCountedThanReservedItems, setLessCountedThanReservedItems] =
    useState<ValidateInventoryResponse>();
  const intl = useIntl();
  const [form] = Form.useForm<ReportInventoryFormValues>();
  const { isMobile } = useWindowSize();
  const history = useHistory();

  const itemsWatch = Form.useWatch<InventoryItemsResponse[]>(
    'items',
    formInventory,
  );
  const inventoryItems = inventoryItemsInfiniteData?.data;

  const processInventory = useAlterStatusInventory();
  const nextCountInventory = useNextCountInventory();
  const validateInventory = useValidateInventory();

  const disable =
    inventory?.status === InventoryStatus.DONE ||
    inventory?.status === InventoryStatus.PROCESSING ||
    inventory?.status === InventoryStatus.IMPORTING;

  const isImporting = inventory?.status === InventoryStatus.IMPORTING;

  const hasNoInventoryItems = (inventoryItems?.length || 0) < 1;

  const disableFinalizeButton =
    (inventory?.status === InventoryStatus.ERROR &&
      !!inventoryItemsInfiniteData?.nextToken) ||
    itemsWatch?.some(
      (teste, index) =>
        inventoryItems &&
        teste.countedQuantity <
          inventoryItems[index]?.stockItemsDTO.stock[0]?.reserved,
    ) ||
    hasNoInventoryItems;

  const generateInventoryReport = useGenerateInventoryReport();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const values = await form.validateFields();

      const obj: ReportInventoryParams = {
        inventoryId: inventoryId,
        filterBy: values.filterBy,
        orderBy: values.orderBy,
        codeFrom: values.codeStart || '',
        codeTo: values.codeEnd || '',
        localizationFrom: values.localizationStart || '',
        localizationTo: values.localizationEnd || '',
        groups: values.groupIds?.toString().replace(' ', ',') || '',
        format: format === FormatType.CSV ? FormatType.CSV : FormatType.PDF,
      };

      const result = await generateInventoryReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const { handleView, handleCsv } = useReportHandlers(
    'relatorio_inventario',
    handleGenerateReport,
  );

  const [reportModalOpen, setReportModalAction] = useDisclosure();
  const [reportP7ModalOpen, setReportP7ModalAction] = useDisclosure();
  const [finalizeInventoryOpen, finalizeInventoryAction] = useDisclosure();

  const handleFinalizeInventory = async (alter: boolean) => {
    if (inventory) {
      const obj: AlterStatusInventory = {
        id: inventory.id,
        status: InventoryStatus.DONE,
        processPrices: alter,
      };

      const result = await validateInventory.mutateAsync(inventory.id);

      if (
        !!result.itemsWithLessCountedThanReserved.length ||
        !result.hasAtLeastOneCheckedItem
      ) {
        setLessCountedThanReservedItems(result);
      } else {
        await processInventory.mutateAsync(obj);
      }

      finalizeInventoryAction.close();
    }
  };

  const handleNextCount = async () => {
    if (inventory) {
      await nextCountInventory.mutateAsync(inventory.id);

      window.scroll(0, 100);
    }
  };

  return (
    <>
      <SpaceForButton>
        <Button block type="ghost" onClick={history.goBack}>
          Voltar
        </Button>

        {!disable && (
          <>
            <Button
              block
              type="primary"
              onClick={handleNextCount}
              loading={nextCountInventory.isLoading}
            >
              {intl.formatMessage({
                id: 'inventory.page.form.modal.processInventory.toNextCount',
              })}
            </Button>

            <Tooltip
              title={
                hasNoInventoryItems
                  ? 'O inventário deve conter ao menos um item antes de ser finalizado'
                  : disableFinalizeButton
                  ? 'Verifique as pendências'
                  : ''
              }
            >
              <Button
                block
                type="primary"
                onClick={finalizeInventoryAction.open}
                style={{
                  backgroundColor: disableFinalizeButton ? undefined : 'green',
                }}
                disabled={disableFinalizeButton}
                loading={
                  processInventory.isLoading || validateInventory.isLoading
                }
              >
                {intl.formatMessage({
                  id: 'inventory.page.form.modal.processInventory.finalizeInventory',
                })}
              </Button>
            </Tooltip>
          </>
        )}

        {!isImporting && (
          <Button block type="primary" onClick={setReportModalAction.open}>
            {intl.formatMessage({ id: 'commons.entity.report' })}
          </Button>
        )}

        {!isImporting && inventory?.currentCount.id && (
          <Button block type="primary" onClick={setReportP7ModalAction.open}>
            Relatório P7
          </Button>
        )}
      </SpaceForButton>

      {inventory?.currentCount.id && (
        <InventoryReportP7Modal
          inventoryCurrentCountId={inventory?.currentCount.id}
          open={reportP7ModalOpen}
          onCancel={setReportP7ModalAction.close}
        />
      )}

      <Modal
        open={reportModalOpen}
        width={900}
        onCancel={() => {
          setReportModalAction.close();
          form.resetFields();
        }}
        destroyOnClose
        footer={
          <SpaceForButton flexDirection={!isMobile ? 'row-reverse' : 'column'}>
            <Button
              block
              onClick={() => {
                setReportModalAction.close();
                form.resetFields();
              }}
            >
              Cancelar
            </Button>

            <Button
              block
              type="primary"
              onClick={handleView}
              key="viewReport"
              loading={fileLoading.REPORT_VIEW}
            >
              Visualizar
            </Button>

            <Button
              block
              type="primary"
              onClick={handleCsv}
              key="CSV"
              loading={fileLoading.CSV}
            >
              Gerar CSV
            </Button>
          </SpaceForButton>
        }
      >
        {(fileLoading.CSV || fileLoading.REPORT_VIEW) && (
          <ModalLoadingOverlay
            messages={['Carregando relatório de inventário...']}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: 8,
            }}
          />
        )}

        <InventoryReportModalForm form={form} />
      </Modal>

      <Modal
        width={860}
        open={finalizeInventoryOpen}
        onCancel={finalizeInventoryAction.close}
        title={'Finalizar Inventário'}
        footer={[
          <SpaceForButton justifyContent="end">
            <Button block onClick={finalizeInventoryAction.close}>
              Cancelar
            </Button>

            <Button
              block
              type="primary"
              onClick={() => handleFinalizeInventory(false)}
            >
              Não alterar valores
            </Button>

            <Button
              block
              type="primary"
              onClick={() => handleFinalizeInventory(true)}
            >
              Alterar valores
            </Button>
          </SpaceForButton>,
        ]}
      >
        Após finalizar o inventário, você deseja alterar os valores das peças?
      </Modal>

      <InventoryLessCountedThanReservedModal
        open={!!lessCountedThanReservedItems}
        onCancel={() => setLessCountedThanReservedItems(undefined)}
        data={lessCountedThanReservedItems}
      />
    </>
  );
};

export default InventoryRepeatButtons;
